import { apiSlice } from "./apiSlice";

const apiSliceWithTags = apiSlice.enhanceEndpoints({
  addTagTypes: ["Subscription", "AIAllocation", "ParentRevenueShare", "ReportCreditShare"],
});

export const subscriptionApiSlice = apiSliceWithTags.injectEndpoints({
  endpoints: (builder) => ({
    // GET /subscription — current plan, usage, wallets
    getSubscription: builder.query({
      query: () => "/subscription",
      providesTags: ["Subscription"],
    }),

    // GET /subscription/plans — all plan definitions (public)
    getPlans: builder.query({
      query: () => "/subscription/plans",
    }),

    // POST /subscription/trial — activate free trial
    startTrial: builder.mutation({
      query: (body) => ({
        url: "/subscription/trial",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/subscribe — returns inline payment config (no redirect)
    // Response: { gateway, reference, amount_naira, config }
    subscribe: builder.mutation({
      query: (body) => ({
        url: "/subscription/subscribe",
        method: "POST",
        body,
      }),
    }),

    // POST /subscription/subscribe/verify — verify payment & activate plan
    verifySubscription: builder.mutation({
      query: (body) => ({
        url: "/subscription/subscribe/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/cancel
    cancelSubscription: builder.mutation({
      query: (body) => ({
        url: "/subscription/cancel",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/change-plan — upgrade (returns inline config) or schedule downgrade
    // For upgrades: response: { action: 'upgrade', gateway, reference, amount_naira, config }
    changePlan: builder.mutation({
      query: (body) => ({
        url: "/subscription/change-plan",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/change-plan/verify — verify upgrade payment
    changePlanVerify: builder.mutation({
      query: (body) => ({
        url: "/subscription/change-plan/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/credits/topup — school PAYG top-up (returns inline payment config)
    // Response: { gateway, reference, amount_naira, config }
    topupCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/credits/topup",
        method: "POST",
        body,
      }),
    }),

    // POST /subscription/credits/verify — verify PAYG top-up payment
    verifyCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/credits/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/ai-credits/purchase — teacher AI credits
    purchaseAiCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/ai-credits/purchase",
        method: "POST",
        body,
      }),
    }),

    // GET /subscription/ai-credits/balance — teacher personal AI wallet
    getAiCreditsBalance: builder.query({
      query: () => "/subscription/ai-credits/balance",
    }),

    // POST /subscription/ai-credits/verify
    verifyAiCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/ai-credits/verify",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/student-credits/purchase — parent buys AI credits for child
    purchaseStudentCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/student-credits/purchase",
        method: "POST",
        body,
      }),
    }),

    // POST /subscription/student-credits/verify
    verifyStudentCredits: builder.mutation({
      query: (body) => ({
        url: "/subscription/student-credits/verify",
        method: "POST",
        body,
      }),
    }),

    // GET /subscription/usage-events — admin activity log
    // Params: page, per_page, action_type (optional filter)
    getUsageEvents: builder.query({
      query: (params) => ({
        url: "/subscription/usage-events",
        params,
      }),
    }),

    // GET /subscription/ai-allocations — list all teacher AI allocations
    getAiAllocations: builder.query({
      query: () => "/subscription/ai-allocations",
      providesTags: ["AIAllocation"],
    }),

    // POST /subscription/ai-allocations — upsert a teacher allocation
    setAiAllocation: builder.mutation({
      query: (body) => ({
        url: "/subscription/ai-allocations",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AIAllocation"],
    }),

    // DELETE /subscription/ai-allocations/:teacher_id — remove an allocation
    deleteAiAllocation: builder.mutation({
      query: (teacher_id) => ({
        url: `/subscription/ai-allocations/${teacher_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AIAllocation"],
    }),

    // GET /subscription/parent-revenue-share — list school's share rows + totals
    getParentRevenueShare: builder.query({
      query: ({ page = 1, per_page = 20 } = {}) => ({
        url: `/subscription/parent-revenue-share?page=${page}&per_page=${per_page}`,
      }),
      providesTags: ["ParentRevenueShare"],
    }),

    // POST /subscription/parent-revenue-share/redeem-payg — convert pending share to PAYG credits
    redeemRevenueShareAsPayg: builder.mutation({
      query: () => ({
        url: "/subscription/parent-revenue-share/redeem-payg",
        method: "POST",
      }),
      invalidatesTags: ["ParentRevenueShare", "Subscription"],
    }),

    // POST /subscription/parent-revenue-share/redeem-cash — request cash payout via email
    redeemRevenueShareAsCash: builder.mutation({
      query: () => ({
        url: "/subscription/parent-revenue-share/redeem-cash",
        method: "POST",
      }),
      invalidatesTags: ["ParentRevenueShare"],
    }),

    // POST /subscription/billing-mode — switch to parent_pays or school_pays
    switchBillingMode: builder.mutation({
      query: (body) => ({
        url: "/subscription/billing-mode",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // POST /subscription/billing-mode/revert — revert pending parent_pays switch
    revertBillingMode: builder.mutation({
      query: () => ({
        url: "/subscription/billing-mode/revert",
        method: "POST",
      }),
      invalidatesTags: ["Subscription"],
    }),

    // PATCH /subscription/parent-pays-config — set report_credit_markup
    updateParentPaysConfig: builder.mutation({
      query: (body) => ({
        url: "/subscription/parent-pays-config",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),

    // GET /subscription/report-credit-share — list school's report credit share rows + totals
    getReportCreditShare: builder.query({
      query: ({ page = 1, per_page = 20 } = {}) => ({
        url: `/subscription/report-credit-share?page=${page}&per_page=${per_page}`,
      }),
      providesTags: ["ReportCreditShare"],
    }),

    // POST /subscription/report-credit-share/redeem-payg — convert earnings to PAYG credits
    redeemReportCreditsAsPayg: builder.mutation({
      query: () => ({
        url: "/subscription/report-credit-share/redeem-payg",
        method: "POST",
      }),
      invalidatesTags: ["ReportCreditShare", "Subscription"],
    }),

    // POST /subscription/report-credit-share/redeem-cash — request cash payout via email
    redeemReportCreditsAsCash: builder.mutation({
      query: () => ({
        url: "/subscription/report-credit-share/redeem-cash",
        method: "POST",
      }),
      invalidatesTags: ["ReportCreditShare"],
    }),

    // POST /subscription/quota/transfer — move quota from one use-case to another
    transferQuota: builder.mutation({
      query: (body) => ({
        url: "/subscription/quota/transfer",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Subscription"],
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useGetPlansQuery,
  useStartTrialMutation,
  useSubscribeMutation,
  useVerifySubscriptionMutation,
  useCancelSubscriptionMutation,
  useChangePlanMutation,
  useChangePlanVerifyMutation,
  useTopupCreditsMutation,
  useVerifyCreditsMutation,
  useLazyGetAiCreditsBalanceQuery,
  usePurchaseAiCreditsMutation,
  useVerifyAiCreditsMutation,
  usePurchaseStudentCreditsMutation,
  useVerifyStudentCreditsMutation,
  useLazyGetUsageEventsQuery,
  useGetAiAllocationsQuery,
  useSetAiAllocationMutation,
  useDeleteAiAllocationMutation,
  useGetParentRevenueShareQuery,
  useRedeemRevenueShareAsPaygMutation,
  useRedeemRevenueShareAsCashMutation,
  useSwitchBillingModeMutation,
  useRevertBillingModeMutation,
  useUpdateParentPaysConfigMutation,
  useGetReportCreditShareQuery,
  useRedeemReportCreditsAsPaygMutation,
  useRedeemReportCreditsAsCashMutation,
  useTransferQuotaMutation,
} = subscriptionApiSlice;
